import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  PieChart,
  TrendingUp,
  TrendingDown,
  Users,
  Activity,
  Star,
  Lightbulb,
  ArrowLeft,
  Calendar,
  Clock,
  Target,
  Award,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Minus,
  ArrowUpRight,
  ArrowDownRight,
  Sparkles,
  CalendarDays
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart as RechartsPie,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';
const COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#d946ef', '#ec4899', '#f43f5e'];
const TREND_COLORS = {
  roi: '#10b981',
  comparisons: '#6366f1',
  impact: '#f59e0b'
};

const ComparisonAnalyticsPage = () => {
  const navigate = useNavigate();
  const { user, currency } = useApp();
  const { isSuperAdmin, viewAsOrg } = useAuth();

  const [personalAnalytics, setPersonalAnalytics] = useState(null);
  const [aggregateAnalytics, setAggregateAnalytics] = useState(null);
  const [trendsData, setTrendsData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTrends, setIsLoadingTrends] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('all');

  // While previewing as a specific org, force-scope every query.
  useEffect(() => {
    if (viewAsOrg?.id) setSelectedOrg(viewAsOrg.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAsOrg?.id]);
  
  // Time period state
  const [trendsPeriod, setTrendsPeriod] = useState('30d');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);

  const orgParam = selectedOrg && selectedOrg !== 'all' ? `organization_id=${selectedOrg}` : '';

  const loadAnalytics = useCallback(async () => {
    try {
      setIsLoading(true);
      const orgP = selectedOrg && selectedOrg !== 'all' ? `organization_id=${selectedOrg}` : '';
      const sep = orgP ? '?' : '';
      const [personalRes, aggregateRes] = await Promise.all([
        api.get(`/comparison-analytics/personal${sep}${orgP}`),
        api.get(`/comparison-analytics/aggregate${sep}${orgP}`)
      ]);
      setPersonalAnalytics(personalRes.data);
      setAggregateAnalytics(aggregateRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  }, [selectedOrg]);

  const loadTrends = useCallback(async () => {
    try {
      setIsLoadingTrends(true);
      const orgP = selectedOrg && selectedOrg !== 'all' ? `organization_id=${selectedOrg}` : '';
      let url = `/comparison-analytics/trends?period=${trendsPeriod}`;
      if (trendsPeriod === 'custom' && customStartDate && customEndDate) {
        url = `/comparison-analytics/trends?start_date=${customStartDate}&end_date=${customEndDate}`;
      }
      if (orgP) url += `&${orgP}`;
      const response = await api.get(url);
      setTrendsData(response.data);
    } catch (error) {
      toast.error('Failed to load trends data');
    } finally {
      setIsLoadingTrends(false);
    }
  }, [trendsPeriod, customStartDate, customEndDate, selectedOrg]);

  useEffect(() => {
    loadAnalytics();
  }, [loadAnalytics]);

  useEffect(() => {
    if (activeTab === 'trends') {
      loadTrends();
    }
  }, [activeTab, loadTrends]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get(`/rbac/organizations`).then(res => {
      setOrganizations(res.data.organizations || []);
    }).catch(() => {});
  }, [isSuperAdmin]);

  const applyCustomDateRange = () => {
    if (customStartDate && customEndDate) {
      setTrendsPeriod('custom');
      setShowCustomDatePicker(false);
      loadTrends();
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const formatFactorName = (factor) => {
    return factor.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Prepare chart data for factor ranking
  const getFactorChartData = (analytics) => {
    if (!analytics?.factor_impact_ranking) return [];
    return analytics.factor_impact_ranking.map(f => ({
      name: f.display_name || formatFactorName(f.factor),
      impact: f.avg_roi_impact || f.avg_impact || 0,
      count: f.comparison_count || f.usage_count || 0
    }));
  };

  // Prepare chart data for preset usage
  const getPresetUsageData = (analytics) => {
    if (!analytics?.popular_presets) return [];
    return analytics.popular_presets.slice(0, 6).map(p => ({
      name: p.name.length > 20 ? p.name.substring(0, 17) + '...' : p.name,
      value: p.usage_count || p.count || 0,
      percentage: p.usage_percent || 0
    }));
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="h-8 w-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Navy');
  const comparisonHeroActions = (
    <>
      {isSuperAdmin && organizations.length > 0 && !viewAsOrg?.id && (
        <select value={selectedOrg} onChange={(e) => setSelectedOrg(e.target.value)}
          className="text-sm rounded-lg px-3 py-1.5 bg-white border border-slate-200 text-slate-800 border-0 focus:ring-2 focus:ring-white/50 min-w-[160px]"
          data-testid="comparison-analytics-org-filter">
          <option value="all">All Organizations</option>
          {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
        </select>
      )}
      {viewAsOrg?.id && (
        <span
          className="text-sm rounded-lg px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 inline-flex items-center gap-1.5"
          data-testid="comparison-analytics-org-filter-locked"
          title="Org filter is locked while previewing — exit preview to widen the view."
        >
          <span className="h-1.5 w-1.5 rounded-full bg-amber-300 animate-pulse" />
          Scoped to {viewAsOrg.name || 'Preview Org'}
        </span>
      )}
      <Button variant="outline" onClick={loadAnalytics} className="bg-white border-slate-200 text-slate-700 hover:bg-slate-50">
        <RefreshCw className="h-4 w-4 mr-2" />
        Refresh
      </Button>
    </>
  );
  const comparisonHeroStats = [
    { label: 'Your Comparisons', value: personalAnalytics?.total_comparisons || 0, testId: 'comparison-stat-yours' },
    { label: 'Avg ROI Diff', value: `${personalAnalytics?.avg_roi_difference > 0 ? '+' : ''}${personalAnalytics?.avg_roi_difference?.toFixed(0) || 0}%`, testId: 'comparison-stat-avg-roi' },
    { label: 'Community Total', value: aggregateAnalytics?.total_comparisons || 0, testId: 'comparison-stat-community' },
    { label: 'Presets Used', value: personalAnalytics?.preset_usage?.length || 0, testId: 'comparison-stat-presets' },
  ];

  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Comparison Analytics"
        description="Insights from your comparisons and community benchmarks. Track your performance and discover patterns."
        theme={__pageTheme}
        icon={PieChart}
        actions={comparisonHeroActions}
        stats={comparisonHeroStats}
        testId="comparison-analytics-hero"
      />
    )}>
    <div className="px-4 md:px-8 lg:px-12 py-6 space-y-6">

      {/* Tab Selector */}
      <div className="flex gap-2">
        <Button
          variant={activeTab === 'personal' ? 'default' : 'outline'}
          onClick={() => setActiveTab('personal')}
          className={activeTab === 'personal' ? 'bg-purple-600 hover:bg-purple-700' : ''}
        >
          <Activity className="h-4 w-4 mr-2" />
          Your Analytics
        </Button>
        <Button
          variant={activeTab === 'trends' ? 'default' : 'outline'}
          onClick={() => setActiveTab('trends')}
          className={activeTab === 'trends' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
        >
          <TrendingUp className="h-4 w-4 mr-2" />
          Trends
        </Button>
        <Button
          variant={activeTab === 'community' ? 'default' : 'outline'}
          onClick={() => setActiveTab('community')}
          className={activeTab === 'community' ? 'bg-teal-600 hover:bg-teal-700' : ''}
        >
          <Users className="h-4 w-4 mr-2" />
          Community Insights
        </Button>
      </div>

      {/* Personal Analytics Tab */}
      {activeTab === 'personal' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-purple-600">{personalAnalytics?.total_comparisons || 0}</p>
                    <p className="text-sm text-slate-500">Total Comparisons</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Target className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-emerald-50 to-emerald-50 border-emerald-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-emerald-600">
                      {personalAnalytics?.avg_roi_difference > 0 ? '+' : ''}{personalAnalytics?.avg_roi_difference?.toFixed(0) || 0}%
                    </p>
                    <p className="text-sm text-slate-500">Avg ROI Difference</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="h-6 w-6 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-blue-600">
                      {formatCurrency(personalAnalytics?.avg_impact_difference || 0, currency)}
                    </p>
                    <p className="text-sm text-slate-500">Avg Impact Diff</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <Activity className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-amber-600">
                      {personalAnalytics?.preset_usage?.length || 0}
                    </p>
                    <p className="text-sm text-slate-500">Presets Used</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Star className="h-6 w-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {personalAnalytics?.total_comparisons > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Factor Impact Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Factor Impact Ranking</CardTitle>
                  <CardDescription>Which factors drive the biggest ROI differences in your comparisons</CardDescription>
                </CardHeader>
                <CardContent>
                  {personalAnalytics?.factor_ranking?.length > 0 ? (
                    <div className="space-y-4">
                      {personalAnalytics.factor_ranking.map(([factor, impact], idx) => (
                        <div key={factor} className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                            idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                            idx === 1 ? 'bg-slate-200 text-slate-700' :
                            idx === 2 ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium text-slate-700">{formatFactorName(factor)}</span>
                              <span className="text-sm font-bold text-purple-600">{(impact || 0).toFixed(0)}%</span>
                            </div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                              <div 
                                className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full"
                                style={{ width: `${Math.min(impact, 100)}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-8">Run more comparisons to see factor rankings</p>
                  )}
                </CardContent>
              </Card>

              {/* Most Used Presets */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Your Most Used Presets</CardTitle>
                  <CardDescription>Presets you frequently use for comparisons</CardDescription>
                </CardHeader>
                <CardContent>
                  {personalAnalytics?.preset_usage?.length > 0 ? (
                    <div className="space-y-3">
                      {personalAnalytics.preset_usage.map((preset, idx) => (
                        <div key={`preset-${preset.name}-${idx}`} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {idx === 0 && <Award className="h-5 w-5 text-yellow-500" />}
                            {idx === 1 && <Award className="h-5 w-5 text-slate-400" />}
                            {idx === 2 && <Award className="h-5 w-5 text-amber-600" />}
                            {idx > 2 && <div className="w-5" />}
                            <span className="font-medium text-slate-700">{preset.name}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{preset.count}x</Badge>
                            <span className="text-sm text-slate-500">{preset.percentage}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-8">Load presets to track usage</p>
                  )}
                </CardContent>
              </Card>

              {/* Recent Comparisons */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Recent Comparisons</CardTitle>
                  <CardDescription>Your latest comparison runs</CardDescription>
                </CardHeader>
                <CardContent>
                  {personalAnalytics?.recent_comparisons?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-2 font-medium text-slate-500">Preset/Type</th>
                            <th className="pb-2 font-medium text-slate-500">Mode</th>
                            <th className="pb-2 font-medium text-slate-500 text-right">ROI Diff</th>
                            <th className="pb-2 font-medium text-slate-500 text-right">Impact Diff</th>
                            <th className="pb-2 font-medium text-slate-500 text-right">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {personalAnalytics.recent_comparisons.slice(0, 5).map((comp, idx) => (
                            <tr key={`comp-${comp.preset_name}-${comp.preset_type}-${idx}`} className="border-b last:border-0">
                              <td className="py-3">
                                <span className="font-medium">{comp.preset_name || 'Custom'}</span>
                                <Badge variant="outline" className="ml-2 text-xs">{comp.preset_type}</Badge>
                              </td>
                              <td className="py-3 capitalize">{comp.analysis_mode}</td>
                              <td className={`py-3 text-right font-medium ${(comp.roi_difference_percent || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {(comp.roi_difference_percent || 0) >= 0 ? '+' : ''}{(comp.roi_difference_percent || 0).toFixed(0)}%
                              </td>
                              <td className="py-3 text-right">{formatCurrency(comp.impact_difference, currency)}</td>
                              <td className="py-3 text-right text-slate-500">
                                {new Date(comp.created_at).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-8">No recent comparisons</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <PieChart className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">No Comparisons Yet</h3>
                <p className="text-slate-500 mb-4">Run your first comparison to start building analytics</p>
                <Button onClick={() => navigate('/dampening-compare')}>
                  Go to Comparison Tool
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Trends Tab */}
      {activeTab === 'trends' && (
        <div className="space-y-6">
          {/* Time Period Selector */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap items-center gap-4">
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-slate-500" />
                  <span className="text-sm font-medium text-slate-700">Time Period:</span>
                </div>
                <div className="flex gap-2">
                  {['7d', '30d', '90d'].map((period) => (
                    <Button
                      key={period}
                      variant={trendsPeriod === period ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setTrendsPeriod(period)}
                      className={trendsPeriod === period ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                    >
                      {period === '7d' ? '7 Days' : period === '30d' ? '30 Days' : '90 Days'}
                    </Button>
                  ))}
                  <Popover open={showCustomDatePicker} onOpenChange={setShowCustomDatePicker}>
                    <PopoverTrigger asChild>
                      <Button
                        variant={trendsPeriod === 'custom' ? 'default' : 'outline'}
                        size="sm"
                        className={trendsPeriod === 'custom' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                      >
                        <Calendar className="h-4 w-4 mr-1" />
                        Custom
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80" align="start">
                      <div className="space-y-4">
                        <h4 className="font-medium text-sm">Custom Date Range</h4>
                        <div className="grid gap-2">
                          <div className="grid gap-1">
                            <Label htmlFor="start-date" className="text-xs">Start Date</Label>
                            <Input
                              id="start-date"
                              type="date"
                              value={customStartDate}
                              onChange={(e) => setCustomStartDate(e.target.value)}
                            />
                          </div>
                          <div className="grid gap-1">
                            <Label htmlFor="end-date" className="text-xs">End Date</Label>
                            <Input
                              id="end-date"
                              type="date"
                              value={customEndDate}
                              onChange={(e) => setCustomEndDate(e.target.value)}
                            />
                          </div>
                        </div>
                        <Button size="sm" className="w-full" onClick={applyCustomDateRange}>
                          Apply Range
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                {isLoadingTrends && (
                  <div className="h-4 w-4 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
                )}
              </div>
            </CardContent>
          </Card>

          {trendsData ? (
            <>
              {/* Natural Language Insights */}
              {trendsData.insights?.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {trendsData.insights.map((insight, idx) => (
                    <Card 
                      key={`insight-${insight.sentiment}-${idx}`} 
                      className={`border-l-4 ${
                        insight.sentiment === 'positive' ? 'border-l-emerald-500 bg-emerald-50/50' :
                        insight.sentiment === 'suggestion' ? 'border-l-amber-500 bg-amber-50/50' :
                        'border-l-slate-300 bg-slate-50/50'
                      }`}
                    >
                      <CardContent className="pt-4 pb-4">
                        <div className="flex items-start gap-3">
                          <div className={`p-2 rounded-full ${
                            insight.sentiment === 'positive' ? 'bg-emerald-100' :
                            insight.sentiment === 'suggestion' ? 'bg-amber-100' :
                            'bg-slate-100'
                          }`}>
                            {insight.icon === 'trending_up' && <TrendingUp className="h-4 w-4 text-emerald-600" />}
                            {insight.icon === 'trending_down' && <TrendingDown className="h-4 w-4 text-rose-500" />}
                            {insight.icon === 'activity' && <Activity className="h-4 w-4 text-purple-600" />}
                            {insight.icon === 'arrow_up' && <ArrowUpRight className="h-4 w-4 text-emerald-600" />}
                            {insight.icon === 'sparkles' && <Sparkles className="h-4 w-4 text-amber-600" />}
                            {insight.icon === 'lightbulb' && <Lightbulb className="h-4 w-4 text-amber-600" />}
                            {insight.icon === 'target' && <Target className="h-4 w-4 text-blue-600" />}
                            {insight.icon === 'minus' && <Minus className="h-4 w-4 text-slate-500" />}
                          </div>
                          <p className="text-sm text-slate-700">{insight.message}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Summary Comparison Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-slate-500">Comparisons</span>
                      <div className={`flex items-center gap-1 text-sm ${
                        trendsData.summary?.trends?.comparison_trend_percent > 0 ? 'text-emerald-600' :
                        trendsData.summary?.trends?.comparison_trend_percent < 0 ? 'text-rose-500' :
                        'text-slate-500'
                      }`}>
                        {trendsData.summary?.trends?.comparison_trend_percent > 0 ? (
                          <ArrowUpRight className="h-4 w-4" />
                        ) : trendsData.summary?.trends?.comparison_trend_percent < 0 ? (
                          <ArrowDownRight className="h-4 w-4" />
                        ) : (
                          <Minus className="h-4 w-4" />
                        )}
                        {Math.abs(trendsData.summary?.trends?.comparison_trend_percent || 0).toFixed(0)}%
                      </div>
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-3xl font-bold text-slate-800">{trendsData.summary?.current_period?.comparisons || 0}</p>
                        <p className="text-xs text-slate-400">This period</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg text-slate-400">{trendsData.summary?.previous_period?.comparisons || 0}</p>
                        <p className="text-xs text-slate-400">Previous</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-slate-500">Avg ROI Difference</span>
                      <div className={`flex items-center gap-1 text-sm ${
                        trendsData.summary?.trends?.roi_trend_percent > 0 ? 'text-emerald-600' :
                        trendsData.summary?.trends?.roi_trend_percent < 0 ? 'text-rose-500' :
                        'text-slate-500'
                      }`}>
                        {trendsData.summary?.trends?.roi_trend_percent > 0 ? (
                          <ArrowUpRight className="h-4 w-4" />
                        ) : trendsData.summary?.trends?.roi_trend_percent < 0 ? (
                          <ArrowDownRight className="h-4 w-4" />
                        ) : (
                          <Minus className="h-4 w-4" />
                        )}
                        {Math.abs(trendsData.summary?.trends?.roi_trend_percent || 0).toFixed(0)}%
                      </div>
                    </div>
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-3xl font-bold text-emerald-600">
                          {trendsData.summary?.current_period?.avg_roi_diff?.toFixed(0) || 0}%
                        </p>
                        <p className="text-xs text-slate-400">This period</p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg text-slate-400">
                          {trendsData.summary?.previous_period?.avg_roi_diff?.toFixed(0) || 0}%
                        </p>
                        <p className="text-xs text-slate-400">Previous</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-sm font-medium text-slate-500">Avg Impact Difference</span>
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-blue-600">
                        {formatCurrency(trendsData.summary?.current_period?.avg_impact_diff || 0, currency)}
                      </p>
                      <p className="text-xs text-slate-400">This period average</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Time Series Chart */}
              {trendsData.time_series?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">ROI Difference Over Time</CardTitle>
                    <CardDescription>Daily average ROI difference from your comparisons</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <AreaChart data={trendsData.time_series}>
                        <defs>
                          <linearGradient id="colorRoi" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                            <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="date" 
                          tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        />
                        <YAxis unit="%" />
                        <Tooltip 
                          labelFormatter={(val) => new Date(val).toLocaleDateString()}
                          formatter={(value) => [`${(value || 0).toFixed(1)}%`, 'Avg ROI Diff']}
                          contentStyle={{ borderRadius: '8px' }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="avg_roi_diff" 
                          stroke="#10b981" 
                          fillOpacity={1} 
                          fill="url(#colorRoi)" 
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              )}

              {/* Factor Evolution */}
              {trendsData.factor_evolution?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Factor Focus Evolution</CardTitle>
                    <CardDescription>How your factor exploration is changing over time</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {trendsData.factor_evolution.filter(f => f.current_count > 0 || f.previous_count > 0).map((factor, idx) => (
                        <div key={factor.factor} className="flex items-center gap-4">
                          <div className="w-40 text-sm font-medium text-slate-700">{factor.display_name}</div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                                <div 
                                  className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all"
                                  style={{ width: `${Math.min((factor.current_count / Math.max(...trendsData.factor_evolution.map(f => f.current_count), 1)) * 100, 100)}%` }}
                                />
                              </div>
                              <span className="text-sm text-slate-600 w-8">{factor.current_count}</span>
                            </div>
                          </div>
                          <div className={`flex items-center gap-1 text-sm min-w-[80px] justify-end ${
                            factor.trend_direction === 'up' ? 'text-emerald-600' :
                            factor.trend_direction === 'down' ? 'text-rose-500' :
                            'text-slate-500'
                          }`}>
                            {factor.trend_direction === 'up' && <ArrowUpRight className="h-4 w-4" />}
                            {factor.trend_direction === 'down' && <ArrowDownRight className="h-4 w-4" />}
                            {factor.trend_direction === 'stable' && <Minus className="h-4 w-4" />}
                            {(factor.trend_percent || 0) > 0 ? '+' : ''}{(factor.trend_percent || 0).toFixed(0)}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Preset Trends */}
              {trendsData.preset_trends?.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Preset Usage Trends</CardTitle>
                    <CardDescription>How your preset preferences are evolving</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {trendsData.preset_trends.map((preset, idx) => (
                        <div key={preset.name} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            {preset.is_new && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200">New</Badge>
                            )}
                            <span className="font-medium text-slate-700">{preset.name}</span>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <span className="text-sm text-slate-600">{preset.current_count}</span>
                              <span className="text-xs text-slate-400 ml-1">this period</span>
                            </div>
                            <div className={`flex items-center gap-1 text-sm min-w-[60px] ${
                              (preset.trend_percent || 0) > 0 ? 'text-emerald-600' :
                              (preset.trend_percent || 0) < 0 ? 'text-rose-500' :
                              'text-slate-500'
                            }`}>
                              {(preset.trend_percent || 0) > 0 && <ArrowUpRight className="h-4 w-4" />}
                              {(preset.trend_percent || 0) < 0 && <ArrowDownRight className="h-4 w-4" />}
                              {(preset.trend_percent || 0) === 0 && <Minus className="h-4 w-4" />}
                              {(preset.trend_percent || 0) > 0 ? '+' : ''}{(preset.trend_percent || 0).toFixed(0)}%
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <TrendingUp className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">No Trend Data Yet</h3>
                <p className="text-slate-500 mb-4">Run comparisons over time to see how your patterns evolve</p>
                <Button onClick={() => navigate('/dampening-compare')}>
                  Start Comparing
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Community Analytics Tab */}
      {activeTab === 'community' && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="bg-gradient-to-br from-teal-50 to-cyan-50 border-teal-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-teal-600">{aggregateAnalytics?.total_comparisons || 0}</p>
                    <p className="text-sm text-slate-500">Total Comparisons</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-teal-100 flex items-center justify-center">
                    <BarChart3 className="h-6 w-6 text-teal-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-indigo-50 to-purple-50 border-indigo-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-indigo-600">{aggregateAnalytics?.unique_users || 0}</p>
                    <p className="text-sm text-slate-500">Active Users</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-indigo-100 flex items-center justify-center">
                    <Users className="h-6 w-6 text-indigo-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-rose-50 to-pink-50 border-rose-100">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-3xl font-bold text-rose-600">{aggregateAnalytics?.popular_presets?.length || 0}</p>
                    <p className="text-sm text-slate-500">Presets in Use</p>
                  </div>
                  <div className="h-12 w-12 rounded-full bg-rose-100 flex items-center justify-center">
                    <Star className="h-6 w-6 text-rose-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {aggregateAnalytics?.total_comparisons > 0 ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Factor Impact Chart - Bar Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Factor Impact Ranking (All Users)</CardTitle>
                  <CardDescription>Which factors drive the biggest ROI differences across all comparisons</CardDescription>
                </CardHeader>
                <CardContent>
                  {aggregateAnalytics?.factor_impact_ranking?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={getFactorChartData(aggregateAnalytics)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" unit="%" />
                        <YAxis dataKey="name" type="category" width={120} />
                        <Tooltip 
                          formatter={(value) => [`${(value || 0).toFixed(1)}%`, 'Avg ROI Impact']}
                          contentStyle={{ borderRadius: '8px' }}
                        />
                        <Bar dataKey="impact" fill="#8b5cf6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-slate-500 py-8">Not enough data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Preset Usage Pie Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Preset Usage Distribution</CardTitle>
                  <CardDescription>Most popular comparison presets across all users</CardDescription>
                </CardHeader>
                <CardContent>
                  {aggregateAnalytics?.popular_presets?.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                      <RechartsPie>
                        <Pie
                          data={getPresetUsageData(aggregateAnalytics)}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                          label={({ name, percentage }) => `${name} (${percentage}%)`}
                        >
                          {getPresetUsageData(aggregateAnalytics).map((entry, index) => (
                            <Cell key={`cell-${entry.name || index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </RechartsPie>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-slate-500 py-8">Not enough data yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Community Insights */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Lightbulb className="h-5 w-5 text-yellow-500" />
                    Community Insights
                  </CardTitle>
                  <CardDescription>Key learnings from aggregated comparison data</CardDescription>
                </CardHeader>
                <CardContent>
                  {aggregateAnalytics?.insights?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {aggregateAnalytics.insights.map((insight, idx) => (
                        <div 
                          key={`agg-insight-${insight.type}-${idx}`} 
                          className={`p-4 rounded-lg border ${
                            insight.type === 'top_factor' ? 'bg-purple-50 border-purple-100' :
                            insight.type === 'popular_preset' ? 'bg-amber-50 border-amber-100' :
                            'bg-emerald-50 border-emerald-100'
                          }`}
                        >
                          <div className="flex items-start gap-3">
                            {insight.type === 'top_factor' && <TrendingUp className="h-5 w-5 text-purple-600 flex-shrink-0" />}
                            {insight.type === 'popular_preset' && <Star className="h-5 w-5 text-amber-600 flex-shrink-0" />}
                            {insight.type === 'recommendation' && <Lightbulb className="h-5 w-5 text-emerald-600 flex-shrink-0" />}
                            <p className="text-sm text-slate-700">{insight.message}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-8">Insights will appear as more data is collected</p>
                  )}
                </CardContent>
              </Card>

              {/* Top Presets Table */}
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Top Performing Presets</CardTitle>
                  <CardDescription>Presets that reveal the largest ROI differences</CardDescription>
                </CardHeader>
                <CardContent>
                  {aggregateAnalytics?.popular_presets?.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b text-left">
                            <th className="pb-2 font-medium text-slate-500">Rank</th>
                            <th className="pb-2 font-medium text-slate-500">Preset Name</th>
                            <th className="pb-2 font-medium text-slate-500">Type</th>
                            <th className="pb-2 font-medium text-slate-500 text-right">Usage</th>
                            <th className="pb-2 font-medium text-slate-500 text-right">Avg ROI Diff</th>
                            <th className="pb-2 font-medium text-slate-500 text-right">Avg Impact Diff</th>
                          </tr>
                        </thead>
                        <tbody>
                          {aggregateAnalytics.popular_presets.slice(0, 10).map((preset, idx) => (
                            <tr key={`popular-${preset.name}-${idx}`} className="border-b last:border-0">
                              <td className="py-3">
                                <span className={`w-6 h-6 inline-flex items-center justify-center rounded-full text-xs font-bold ${
                                  idx === 0 ? 'bg-yellow-100 text-yellow-700' :
                                  idx === 1 ? 'bg-slate-200 text-slate-700' :
                                  idx === 2 ? 'bg-amber-100 text-amber-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                  {idx + 1}
                                </span>
                              </td>
                              <td className="py-3 font-medium">{preset.name}</td>
                              <td className="py-3">
                                <Badge variant={preset.type === 'system' ? 'default' : 'outline'}>
                                  {preset.type}
                                </Badge>
                              </td>
                              <td className="py-3 text-right">{preset.usage_count} ({preset.usage_percent}%)</td>
                              <td className={`py-3 text-right font-medium ${(preset.avg_roi_difference || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {(preset.avg_roi_difference || 0) >= 0 ? '+' : ''}{(preset.avg_roi_difference || 0).toFixed(0)}%
                              </td>
                              <td className="py-3 text-right">{formatCurrency(preset.avg_impact_difference, currency)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="text-center text-slate-500 py-8">No preset data available yet</p>
                  )}
                </CardContent>
              </Card>
            </div>
          ) : (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <Users className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700 mb-2">No Community Data Yet</h3>
                <p className="text-slate-500 mb-4">Be the first to contribute to community insights!</p>
                <Button onClick={() => navigate('/dampening-compare')}>
                  Start Comparing
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
    </PageShell>
  );
};

export default ComparisonAnalyticsPage;
